import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../logo.png';

/* ─── Animated counter ─── */
function AnimatedCounter({ end, suffix = '', prefix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const step = end / (duration / 16);
          let current = 0;
          const timer = setInterval(() => {
            current += step;
            if (current >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(current));
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ─── Scroll-reveal wrapper ─── */
function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─── Feature data ─── */
const modules = [
  { icon: '📊', title: 'Dashboard & Analytics', desc: 'Real-time business overview with revenue charts, top products, payment trends, and KPI cards all in one glance.' },
  { icon: '🧾', title: 'GST Billing & Invoicing', desc: 'Create professional GST-compliant invoices with auto tax calculation, multi-item support, discounts, and instant PDF generation.' },
  { icon: '📦', title: 'Inventory Management', desc: 'Track stock levels, set low-stock alerts, manage batches, categories, HSN codes, and warehouse locations effortlessly.' },
  { icon: '👥', title: 'Customer & Supplier CRM', desc: 'Maintain detailed customer profiles, purchase history, outstanding balances, VCF import, and smart search with autocomplete.' },
  { icon: '💰', title: 'Payments & Ledger', desc: 'Record payments, track outstanding dues, maintain party-wise ledgers, and handle credit/debit notes with full audit trail.' },
  { icon: '📱', title: 'WhatsApp Integration', desc: 'Send invoices, payment reminders, and promotional messages directly via WhatsApp with one-click sharing.' },
  { icon: '📈', title: 'Reports & GST Filing', desc: 'Generate sales reports, purchase summaries, profit/loss statements, GSTR-1, GSTR-3B ready reports, and tax summaries.' },
  { icon: '🏪', title: 'Multi-Store / Branches', desc: 'Manage multiple store locations with centralized control, branch-wise reporting, and inter-branch stock transfers.' },
  { icon: '🔐', title: 'Staff & Role Management', desc: 'Create staff accounts with fine-grained permissions. Owner, manager, and staff roles with activity logging.' },
  { icon: '💼', title: 'Quotation Management', desc: 'Create and send professional quotations. Convert accepted quotes to invoices with a single click.' },
  { icon: '🏭', title: 'Warehouse & Godowns', desc: 'Manage multiple warehouses, track stock movement between locations, and optimize storage utilization.' },
  { icon: '📊', title: 'Accounting Module', desc: 'Complete double-entry accounting with journal entries, trial balance, balance sheet, and P&L statements.' },
  { icon: '🔧', title: 'AMC Service Tracking', desc: 'Track Annual Maintenance Contracts with renewal alerts, service history, and customer-wise AMC dashboards.' },
  { icon: '📣', title: 'Marketing Campaigns', desc: 'Run targeted SMS and WhatsApp marketing campaigns with customer segmentation and delivery tracking.' },
  { icon: '👨‍💼', title: 'HR & Attendance', desc: 'Manage employee records, attendance tracking, leave management, and basic payroll calculations.' },
  { icon: '☁️', title: 'Cloud Backups', desc: 'Automated local and Google Drive backups every 6 hours. One-click restore with versioned backup history.' },
  { icon: '🎨', title: 'Custom Branding', desc: 'Upload your shop logo, set brand colors, and customize invoice headers. Your brand, your identity.' },
  { icon: '🛡️', title: 'Enterprise Security', desc: 'Multi-tenant Row-Level Security, encrypted credentials, JWT auth, per-client data isolation, and audit logs.' },
];

const pricing = [
  {
    name: 'Starter',
    price: '₹499',
    period: '/month',
    desc: 'Perfect for small shops just getting started',
    features: ['1 Store', 'GST Billing & Invoicing', 'Inventory (up to 500 products)', 'Customer Management', 'Payment Tracking', 'Basic Reports', 'WhatsApp Invoice Sharing', 'Cloud Backups', 'Email Support'],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Professional',
    price: '₹999',
    period: '/month',
    desc: 'For growing businesses that need more power',
    features: ['Up to 3 Stores', 'Everything in Starter', 'Unlimited Products', 'Quotation Management', 'Advanced Reports & GST Filing', 'Marketing Campaigns', 'Staff Management (5 users)', 'Warehouse Management', 'AMC Tracking', 'Priority Support'],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Enterprise',
    price: '₹1,999',
    period: '/month',
    desc: 'Full-featured solution for large operations',
    features: ['Unlimited Stores & Branches', 'Everything in Professional', 'Unlimited Staff Accounts', 'HR & Attendance Module', 'Full Accounting Suite', 'CRM with Advanced Analytics', 'Google Drive Auto-Backup', 'Custom Branding', 'API Access', 'Dedicated Account Manager'],
    cta: 'Contact Sales',
    popular: false,
  },
];

const testimonials = [
  { name: 'Rajesh Patel', business: 'Patel Electronics, Ahmedabad', quote: 'Smart Vyapari transformed how we manage our shop. GST billing that used to take hours now takes minutes!', avatar: '👨‍💼' },
  { name: 'Priya Sharma', business: 'Sharma Textiles, Jaipur', quote: 'The WhatsApp integration is a game changer. My customers love receiving digital invoices instantly.', avatar: '👩‍💼' },
  { name: 'Amit Kumar', business: 'Kumar Hardware, Pune', quote: 'Managing 3 branches used to be a nightmare. Now I track everything from one dashboard. Highly recommended!', avatar: '👨‍🔧' },
];

/* ─── Landing Component ─── */
const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const faqs = [
    { q: 'Is my data secure?', a: 'Absolutely. We use enterprise-grade Row-Level Security in PostgreSQL, encrypted credentials, JWT authentication, and per-client data isolation. Your data is never accessible to other users.' },
    { q: 'Can I use it on mobile?', a: 'Yes! Smart Vyapari is fully responsive and works beautifully on phones, tablets, and desktops. Access your business data from anywhere.' },
    { q: 'Do you support GST compliance?', a: 'Yes — our billing engine auto-calculates CGST, SGST, and IGST. We generate GSTR-1 and GSTR-3B ready reports for hassle-free filing.' },
    { q: 'Can I switch plans later?', a: 'Yes, you can upgrade or downgrade your plan anytime. Changes take effect from the next billing cycle, no hidden charges.' },
    { q: 'Is there a free trial?', a: 'Yes! All plans come with a 14-day free trial. No credit card required. Start using Smart Vyapari today.' },
  ];

  const accent = '#6366F1';
  const accentLight = '#818CF8';

  return (
    <div style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif", color: '#0F172A', overflowX: 'hidden' }}>

      {/* ─── CSS Animations ─── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes pulse-glow { 0%,100%{box-shadow:0 0 20px rgba(99,102,241,0.3)} 50%{box-shadow:0 0 40px rgba(99,102,241,0.5)} }
        @keyframes gradient-shift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes slide-in-left { from{opacity:0;transform:translateX(-40px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slide-in-right { from{opacity:0;transform:translateX(40px)} to{opacity:1;transform:translateX(0)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }

        .landing-nav { transition: all 0.3s ease; }
        .landing-nav.scrolled { background: rgba(255,255,255,0.95) !important; backdrop-filter: blur(20px); box-shadow: 0 1px 20px rgba(0,0,0,0.08) !important; }
        .nav-link { color: #475569; font-weight: 500; text-decoration: none; font-size: 15px; transition: color 0.2s; cursor: pointer; }
        .nav-link:hover { color: #6366F1; }

        .hero-gradient {
          background: linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 25%, #F5F3FF 50%, #EFF6FF 75%, #F0FDFA 100%);
          background-size: 300% 300%;
          animation: gradient-shift 8s ease infinite;
        }

        .feature-card {
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 16px;
          padding: 28px 24px;
          transition: all 0.3s ease;
          cursor: default;
        }
        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(99,102,241,0.12);
          border-color: #C7D2FE;
        }

        .pricing-card {
          background: #fff;
          border: 1px solid #E2E8F0;
          border-radius: 20px;
          padding: 36px 28px;
          transition: all 0.3s ease;
          position: relative;
        }
        .pricing-card:hover { transform: translateY(-6px); box-shadow: 0 24px 48px rgba(0,0,0,0.1); }
        .pricing-card.popular { border: 2px solid #6366F1; }

        .cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 32px; border-radius: 12px; border: none;
          font-weight: 700; font-size: 16px; cursor: pointer;
          transition: all 0.3s ease; text-decoration: none;
        }
        .cta-btn:hover { transform: translateY(-2px); }
        .cta-primary { background: #6366F1; color: #fff; box-shadow: 0 4px 20px rgba(99,102,241,0.4); }
        .cta-primary:hover { background: #4F46E5; box-shadow: 0 8px 30px rgba(99,102,241,0.5); }
        .cta-secondary { background: #fff; color: #6366F1; border: 2px solid #6366F1; }
        .cta-secondary:hover { background: #EEF2FF; }

        .testimonial-card {
          background: #fff; border-radius: 16px; padding: 28px;
          border: 1px solid #E2E8F0; transition: all 0.3s ease;
        }
        .testimonial-card:hover { box-shadow: 0 12px 32px rgba(0,0,0,0.08); }

        .faq-item { border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden; transition: all 0.2s; }
        .faq-item:hover { border-color: #C7D2FE; }

        .mobile-menu {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 100;
          background: rgba(255,255,255,0.98); backdrop-filter: blur(20px);
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 24px;
        }

        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-btn { display: flex !important; }
          .hero-grid { flex-direction: column !important; text-align: center; }
          .hero-text h1 { font-size: 36px !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .pricing-grid { grid-template-columns: 1fr !important; }
          .testimonials-grid { grid-template-columns: 1fr !important; }
          .stats-grid { grid-template-columns: 1fr 1fr !important; }
          .footer-grid { grid-template-columns: 1fr !important; }
          .section-padding { padding: 60px 16px !important; }
        }
        @media (min-width: 769px) {
          .mobile-btn { display: none !important; }
        }
      `}</style>

      {/* ─── Navigation ─── */}
      <nav
        className={`landing-nav ${scrolled ? 'scrolled' : ''}`}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 32px', maxWidth: 1280, margin: '0 auto',
          background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
        }}
      >
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 49, height: 64, background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent', backdropFilter: scrolled ? 'blur(20px)' : 'none', transition: 'all 0.3s', boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none' }} />
        <div style={{ position: 'relative', zIndex: 51, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logo} alt="Smart Vyapari" style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover' }} />
            <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: '-0.03em', color: '#0F172A' }}>Smart Vyapari</span>
          </div>

          <div className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            <span className="nav-link" onClick={() => scrollTo('features')}>Features</span>
            <span className="nav-link" onClick={() => scrollTo('pricing')}>Pricing</span>
            <span className="nav-link" onClick={() => scrollTo('testimonials')}>Testimonials</span>
            <span className="nav-link" onClick={() => scrollTo('faq')}>FAQ</span>
            <button onClick={() => navigate('/login')} className="cta-btn cta-primary" style={{ padding: '10px 24px', fontSize: 14 }}>
              Login / Sign Up →
            </button>
          </div>

          <button
            className="mobile-btn"
            onClick={() => setMobileMenu(v => !v)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}
          >
            <span style={{ width: 24, height: 2.5, background: '#0F172A', borderRadius: 2, transition: 'all 0.3s', transform: mobileMenu ? 'rotate(45deg) translateY(7.5px)' : 'none' }} />
            <span style={{ width: 24, height: 2.5, background: '#0F172A', borderRadius: 2, transition: 'all 0.3s', opacity: mobileMenu ? 0 : 1 }} />
            <span style={{ width: 24, height: 2.5, background: '#0F172A', borderRadius: 2, transition: 'all 0.3s', transform: mobileMenu ? 'rotate(-45deg) translateY(-7.5px)' : 'none' }} />
          </button>
        </div>
      </nav>

      {/* ─── Mobile Menu ─── */}
      {mobileMenu && (
        <div className="mobile-menu">
          <button onClick={() => setMobileMenu(false)} style={{ position: 'absolute', top: 16, right: 24, background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#475569' }}>✕</button>
          {['features', 'pricing', 'testimonials', 'faq'].map(id => (
            <span key={id} className="nav-link" style={{ fontSize: 22, fontWeight: 600 }} onClick={() => scrollTo(id)}>
              {id.charAt(0).toUpperCase() + id.slice(1)}
            </span>
          ))}
          <button onClick={() => { setMobileMenu(false); navigate('/login'); }} className="cta-btn cta-primary" style={{ marginTop: 12 }}>
            Login / Sign Up →
          </button>
        </div>
      )}

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="hero-gradient animate-fadeIn" style={{ paddingTop: 100, paddingBottom: 80 }}>
        <div className="hero-grid section-padding" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 32px', display: 'flex', alignItems: 'center', gap: 60 }}>
          <div className="hero-text" style={{ flex: 1, animation: 'slide-in-left 0.8s ease' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 100, padding: '6px 16px', marginBottom: 20, fontSize: 13, fontWeight: 600, color: accent }}>
              🚀 #1 Billing Software for Indian Shops
            </div>
            <h1 style={{ fontSize: 52, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.04em', marginBottom: 20, color: '#0F172A' }}>
              Smart software for{' '}
              <span style={{ background: `linear-gradient(135deg, ${accent}, #A855F7)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                smart business
              </span>{' '}
              owners.
            </h1>
            <p style={{ fontSize: 18, color: '#475569', lineHeight: 1.7, marginBottom: 32, maxWidth: 520 }}>
              All-in-one GST billing, inventory, CRM, accounting, and multi-store management — built specifically for Indian shop owners. Start in minutes, not days.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/login')} className="cta-btn cta-primary" style={{ fontSize: 17 }}>
                Start Free Trial →
              </button>
              <button onClick={() => scrollTo('features')} className="cta-btn cta-secondary">
                Explore Features
              </button>
            </div>
            <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {['No credit card required', '14-day free trial', 'Cancel anytime'].map(t => (
                <span key={t} style={{ fontSize: 13, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#22C55E', fontWeight: 700 }}>✓</span> {t}
                </span>
              ))}
            </div>
          </div>

          <div style={{ flex: 1, animation: 'slide-in-right 0.8s ease', display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: '100%', maxWidth: 480, aspectRatio: '4/3', borderRadius: 20,
              background: 'linear-gradient(145deg, #1E293B, #0F172A)',
              padding: 24, position: 'relative', overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.25)', animation: 'float 4s ease-in-out infinite',
            }}>
              {/* Mock dashboard */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444' }} />
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B' }} />
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22C55E' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Revenue', value: '₹4,52,890', color: '#22C55E', icon: '📈' },
                  { label: 'Orders', value: '1,234', color: '#3B82F6', icon: '🧾' },
                  { label: 'Products', value: '856', color: '#A855F7', icon: '📦' },
                  { label: 'Customers', value: '342', color: '#F59E0B', icon: '👥' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>{s.icon} {s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 8 }}>Revenue Trend</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 60 }}>
                  {[40, 55, 35, 70, 50, 85, 65, 90, 75, 95, 80, 100].map((h, i) => (
                    <div key={i} style={{ flex: 1, height: `${h}%`, background: `linear-gradient(to top, ${accent}, ${accentLight})`, borderRadius: 4, opacity: 0.7 + (i * 0.025) }} />
                  ))}
                </div>
              </div>
              {/* Decorative glow */}
              <div style={{ position: 'absolute', top: -50, right: -50, width: 150, height: 150, borderRadius: '50%', background: `radial-gradient(circle, ${accent}33, transparent)` }} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ STATS ═══════════════════ */}
      <section style={{ background: '#fff', borderBottom: '1px solid #F1F5F9' }}>
        <div className="section-padding stats-grid" style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 32px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32, textAlign: 'center' }}>
          {[
            { value: 500, suffix: '+', label: 'Active Shops' },
            { value: 50000, suffix: '+', label: 'Invoices Generated' },
            { prefix: '₹', value: 10, suffix: 'Cr+', label: 'Revenue Managed' },
            { value: 99.9, suffix: '%', label: 'Uptime SLA' },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 100}>
              <div style={{ fontSize: 36, fontWeight: 900, color: accent, letterSpacing: '-0.02em' }}>
                <AnimatedCounter end={s.value} suffix={s.suffix} prefix={s.prefix || ''} />
              </div>
              <div style={{ fontSize: 14, color: '#64748B', fontWeight: 500, marginTop: 4 }}>{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══════════════════ FEATURES ═══════════════════ */}
      <section id="features" style={{ background: '#F8FAFC' }}>
        <div className="section-padding" style={{ maxWidth: 1200, margin: '0 auto', padding: '80px 32px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <span style={{ display: 'inline-block', background: '#EEF2FF', color: accent, padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 700, marginBottom: 12, border: '1px solid #C7D2FE' }}>FEATURES</span>
              <h2 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 12 }}>Everything your business needs</h2>
              <p style={{ fontSize: 17, color: '#64748B', maxWidth: 600, margin: '0 auto' }}>18+ powerful modules designed for Indian shop owners — from billing to HR, all in one platform.</p>
            </div>
          </Reveal>

          <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {modules.map((m, i) => (
              <Reveal key={i} delay={(i % 3) * 100}>
                <div className="feature-card">
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{m.icon}</div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: '#0F172A' }}>{m.title}</h3>
                  <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6 }}>{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
      <section style={{ background: '#fff' }}>
        <div className="section-padding" style={{ maxWidth: 900, margin: '0 auto', padding: '80px 32px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <span style={{ display: 'inline-block', background: '#F0FDF4', color: '#16A34A', padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 700, marginBottom: 12, border: '1px solid #BBF7D0' }}>HOW IT WORKS</span>
              <h2 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 12 }}>Up and running in 3 steps</h2>
            </div>
          </Reveal>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {[
              { step: '01', title: 'Sign up & configure your shop', desc: 'Create your account, add your shop name, GST number, logo, and basic details. Takes under 2 minutes.', icon: '🏪' },
              { step: '02', title: 'Add products & customers', desc: 'Import your product catalog and customer list. Use CSV import or add them manually with smart autocomplete.', icon: '📋' },
              { step: '03', title: 'Start billing & grow', desc: 'Create GST invoices, track payments, send WhatsApp receipts, and watch your business analytics grow.', icon: '🚀' },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 150}>
                <div style={{
                  display: 'flex', gap: 24, alignItems: 'flex-start',
                  background: '#F8FAFC', borderRadius: 16, padding: 28,
                  border: '1px solid #E2E8F0',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14, flexShrink: 0,
                    background: `linear-gradient(135deg, ${accent}, ${accentLight})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 900, color: '#fff',
                  }}>{s.step}</div>
                  <div>
                    <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: '#0F172A' }}>{s.icon} {s.title}</h3>
                    <p style={{ fontSize: 15, color: '#64748B', lineHeight: 1.6 }}>{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRICING ═══════════════════ */}
      <section id="pricing" style={{ background: '#F8FAFC' }}>
        <div className="section-padding" style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 32px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <span style={{ display: 'inline-block', background: '#FEF3C7', color: '#D97706', padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 700, marginBottom: 12, border: '1px solid #FDE68A' }}>PRICING</span>
              <h2 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 12 }}>Simple, transparent pricing</h2>
              <p style={{ fontSize: 17, color: '#64748B', maxWidth: 500, margin: '0 auto' }}>Start free for 14 days. No credit card required. Choose the plan that fits your business.</p>
            </div>
          </Reveal>

          <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, alignItems: 'start' }}>
            {pricing.map((p, i) => (
              <Reveal key={i} delay={i * 150}>
                <div className={`pricing-card ${p.popular ? 'popular' : ''}`} style={{ transform: p.popular ? 'scale(1.04)' : 'none' }}>
                  {p.popular && (
                    <div style={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      background: accent, color: '#fff', padding: '5px 20px', borderRadius: 100,
                      fontSize: 12, fontWeight: 700, letterSpacing: '0.05em',
                    }}>MOST POPULAR</div>
                  )}
                  <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4, color: '#0F172A' }}>{p.name}</h3>
                  <p style={{ fontSize: 13, color: '#64748B', marginBottom: 20 }}>{p.desc}</p>
                  <div style={{ marginBottom: 24 }}>
                    <span style={{ fontSize: 42, fontWeight: 900, color: '#0F172A', letterSpacing: '-0.03em' }}>{p.price}</span>
                    <span style={{ fontSize: 15, color: '#94A3B8' }}>{p.period}</span>
                  </div>
                  <button
                    onClick={() => navigate('/login')}
                    className="cta-btn"
                    style={{
                      width: '100%', justifyContent: 'center', marginBottom: 24,
                      background: p.popular ? accent : '#F1F5F9',
                      color: p.popular ? '#fff' : '#475569',
                      boxShadow: p.popular ? `0 4px 20px ${accent}44` : 'none',
                      fontWeight: 700,
                    }}
                  >{p.cta}</button>
                  <ul style={{ listStyle: 'none', padding: 0 }}>
                    {p.features.map((f, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, fontSize: 14, color: '#475569' }}>
                        <span style={{ color: '#22C55E', fontWeight: 700, marginTop: 1, flexShrink: 0 }}>✓</span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ TESTIMONIALS ═══════════════════ */}
      <section id="testimonials" style={{ background: '#fff' }}>
        <div className="section-padding" style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 32px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 56 }}>
              <span style={{ display: 'inline-block', background: '#FCE7F3', color: '#DB2777', padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 700, marginBottom: 12, border: '1px solid #FBCFE8' }}>TESTIMONIALS</span>
              <h2 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 12 }}>Loved by shop owners</h2>
            </div>
          </Reveal>

          <div className="testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 150}>
                <div className="testimonial-card">
                  <div style={{ fontSize: 28, color: '#E2E8F0', marginBottom: 12, fontFamily: 'Georgia, serif' }}>"</div>
                  <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.7, marginBottom: 20 }}>{t.quote}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{t.avatar}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8' }}>{t.business}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section id="faq" style={{ background: '#F8FAFC' }}>
        <div className="section-padding" style={{ maxWidth: 720, margin: '0 auto', padding: '80px 32px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <span style={{ display: 'inline-block', background: '#ECFDF5', color: '#059669', padding: '6px 16px', borderRadius: 100, fontSize: 13, fontWeight: 700, marginBottom: 12, border: '1px solid #A7F3D0' }}>FAQ</span>
              <h2 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em' }}>Frequently asked questions</h2>
            </div>
          </Reveal>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="faq-item" style={{ background: '#fff' }}>
                  <button
                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                    style={{
                      width: '100%', padding: '18px 20px', background: 'none', border: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#0F172A', textAlign: 'left',
                    }}
                  >
                    {f.q}
                    <span style={{
                      fontSize: 20, color: '#94A3B8', transition: 'transform 0.3s',
                      transform: activeFaq === i ? 'rotate(45deg)' : 'none',
                    }}>+</span>
                  </button>
                  <div style={{
                    maxHeight: activeFaq === i ? 200 : 0, overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}>
                    <p style={{ padding: '0 20px 18px', fontSize: 14, color: '#64748B', lineHeight: 1.7 }}>{f.a}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ CTA BANNER ═══════════════════ */}
      <section style={{ background: `linear-gradient(135deg, #1E293B, #0F172A)` }}>
        <div className="section-padding" style={{ maxWidth: 800, margin: '0 auto', padding: '80px 32px', textAlign: 'center' }}>
          <Reveal>
            <h2 style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', marginBottom: 14 }}>
              Ready to transform your business?
            </h2>
            <p style={{ fontSize: 17, color: '#94A3B8', marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
              Join hundreds of shop owners who switched to Smart Vyapari. Start your 14-day free trial today.
            </p>
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/login')} className="cta-btn cta-primary" style={{ fontSize: 17, animation: 'pulse-glow 2s ease-in-out infinite' }}>
                Get Started Free →
              </button>
              <button onClick={() => scrollTo('pricing')} className="cta-btn" style={{ background: 'rgba(255,255,255,0.1)', color: '#E2E8F0', border: '1px solid rgba(255,255,255,0.2)' }}>
                View Pricing
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer style={{ background: '#0F172A', borderTop: '1px solid #1E293B' }}>
        <div className="section-padding footer-grid" style={{ maxWidth: 1100, margin: '0 auto', padding: '56px 32px 32px', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 40 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <img src={logo} alt="Smart Vyapari" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover' }} />
              <span style={{ fontWeight: 800, fontSize: 18, color: '#F1F5F9' }}>Smart Vyapari</span>
            </div>
            <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.7, maxWidth: 280 }}>
              India's smartest billing & business management platform. Built with ♥ for shop owners.
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Features', 'Pricing', 'Testimonials', 'FAQ'].map(l => (
                <span key={l} onClick={() => scrollTo(l.toLowerCase())} style={{ color: '#64748B', fontSize: 14, cursor: 'pointer', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.target.style.color = '#F1F5F9'} onMouseLeave={e => e.target.style.color = '#64748B'}>{l}</span>
              ))}
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Modules</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['GST Billing', 'Inventory', 'CRM', 'Reports', 'Accounting'].map(l => (
                <span key={l} style={{ color: '#64748B', fontSize: 14 }}>{l}</span>
              ))}
            </div>
          </div>
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ color: '#64748B', fontSize: 14 }}>✉ komarewargaurav@gmail.com</span>
              <span style={{ color: '#64748B', fontSize: 14 }}>📱 +91 88300 77989</span>
              <span style={{ color: '#64748B', fontSize: 14 }}>@Gaurav_Komarewar</span>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 32px', borderTop: '1px solid #1E293B', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#475569' }}>© {new Date().getFullYear()} Smart Vyapari. All rights reserved.</span>
          <span style={{ fontSize: 13, color: '#475569' }}>Made with ♥ in India</span>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
